import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

/**
 * Accessible horizontal scroll rail for Solutions (needs / flagship).
 * No autoplay. Touch/swipe via native overflow. Keyboard arrows supported.
 * Namespaced separately from Home carousel — Home freeze preserved.
 */
export function SolutionsScrollRail({
  label,
  prevLabel,
  nextLabel,
  children,
  className = "",
  showDots = true,
}: {
  label: string;
  prevLabel: string;
  nextLabel: string;
  children: ReactNode;
  className?: string;
  showDots?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const labelId = useId();

  const sync = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slides = [...track.querySelectorAll<HTMLElement>("[data-sol-slide]")];
    setTotal(slides.length);
    if (slides.length === 0) return;
    const left = track.scrollLeft;
    let closest = 0;
    let best = Number.POSITIVE_INFINITY;
    slides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - left);
      if (dist < best) {
        best = dist;
        closest = i;
      }
    });
    const maxScroll = track.scrollWidth - track.clientWidth;
    setIndex(closest);
    setCanPrev(left > 4);
    setCanNext(left < maxScroll - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    sync();
    track.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(() => sync());
    ro.observe(track);
    return () => {
      track.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [sync, children]);

  const scrollToIndex = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slides = track.querySelectorAll<HTMLElement>("[data-sol-slide]");
    const target = slides[Math.max(0, Math.min(next, slides.length - 1))];
    if (!target) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left: target.offsetLeft, behavior: reduce ? "auto" : "smooth" });
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollToIndex(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollToIndex(index + 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      scrollToIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      scrollToIndex(total - 1);
    }
  };

  return (
    <div
      className={`pep-sol-rail ${className}`.trim()}
      data-carousel="solutions-rail"
      onKeyDown={onKeyDown}
    >
      <p id={labelId} className="visually-hidden">
        {label}
      </p>
      <div className="pep-sol-rail__toolbar" role="group" aria-labelledby={labelId}>
        <button
          type="button"
          className="pep-sol-rail__btn"
          onClick={() => scrollToIndex(index - 1)}
          disabled={!canPrev}
          aria-label={prevLabel}
        >
          ‹
        </button>
        <button
          type="button"
          className="pep-sol-rail__btn"
          onClick={() => scrollToIndex(index + 1)}
          disabled={!canNext}
          aria-label={nextLabel}
        >
          ›
        </button>
      </div>
      <div
        ref={trackRef}
        className="pep-sol-rail__track"
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-labelledby={labelId}
      >
        {children}
      </div>
      {showDots && total > 1 ? (
        <div className="pep-sol-rail__dots" aria-hidden="true">
          {Array.from({ length: total }, (_, i) => (
            <span key={i} className={`pep-sol-rail__dot${i === index ? " is-active" : ""}`} />
          ))}
        </div>
      ) : null}
      <p className="visually-hidden" aria-live="polite">
        {total > 0 ? `${index + 1} / ${total}` : ""}
      </p>
    </div>
  );
}
